import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useJob } from "./api";
import type { MikrotikJob } from "./schemas";

type Options = {
  onComplete?: (job: MikrotikJob) => void;
  onError?: (job: MikrotikJob) => void;
  /** Query keys a invalidar cuando el job termine. */
  invalidate?: readonly (readonly unknown[])[];
};

/**
 * Hook que polea un job y dispara callbacks al completar/fallar.
 * Previene doble disparo del mismo job entre re-renders.
 */
export function useJobPolling(
  jobId: string | null | undefined,
  { onComplete, onError, invalidate }: Options = {}
) {
  const qc = useQueryClient();
  const firedRef = useRef<string | null>(null);
  const { data: job, isLoading, error } = useJob(jobId ?? null);

  useEffect(() => {
    if (!job) return;
    // Solo dispara callback UNA vez por job
    if (firedRef.current === job.id) return;
    if (job.status === "completed" || job.status === "error") {
      firedRef.current = job.id;
      if (invalidate) {
        for (const key of invalidate) {
          qc.invalidateQueries({ queryKey: key as unknown[] });
        }
      }
      if (job.status === "completed") onComplete?.(job);
      else onError?.(job);
    }
  }, [job, invalidate, onComplete, onError, qc]);

  return { job, isLoading, error };
}
